import React from "react";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {Box, Modal, Typography} from "@mui/material";
import {PrimaryButton, PrimaryButtonInvert, ShareButton} from "views/components/style";
import {ReactComponent as CloseIcon} from "assets/img/icons/modal/close.svg";
import {ReactComponent as ShareIcon} from "assets/img/icons/icon_native_share.svg";
import {ReactComponent as CopyIcon} from "assets/img/icons/icon_copy.svg";
import {ReactComponent as FacebookIcon} from "assets/img/icons/social/facebook-icon.svg";
import {ReactComponent as TwitterIcon} from "assets/img/icons/social/twitter-icon.svg";
import styled from "@emotion/styled";
import type {IShareController} from "views/controllers/share/share.controller";
import {Exist} from "views/components/exist/exist.component";
import {Preloader} from "views/components/preloader";
import {SITE_URL} from "data/constants";
import {partial} from "lodash";

const ModalContent = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: center;
	gap: 6px;
	outline: none;
	max-width: 600px;
	width: 100%;
	background: #fff;
	padding: 40px 16px 16px;
	border-radius: 4px;
	text-align: center;

	@media screen and (max-width: 640px) {
		max-width: unset;
		left: 24px;
		right: 24px;
		width: auto;
		transform: translateY(-50%);
	}

	button {
		max-width: 300px;
	}

	h3 {
		color: #103365;
		text-align: center;
		font-family: var(--fontFamilyBase);
		font-size: 20px;
		font-style: normal;
		font-weight: 700;
		line-height: 130%; /* 26px */
	}
`;

const CloseBlock = styled.div`
	width: 24px;
	height: 24px;
	display: flex;
	justify-content: flex-end;
	align-items: center;
	position: absolute;
	top: 20px;
	right: 20px;
	cursor: pointer;
`;

const Description = styled("p")`
	color: #151515;
	font-family: var(--fontFamilyBase);
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 140%;

	b {
		font-weight: 700;
	}
`;

const ImgBox = styled(Box)`
	width: 100%;
	margin: 6px 0;

	//img {
	//	display: block;
	//	width: 100%;
	//}
`;
const ButtonRow = styled(Box)`
	width: 100%;
	display: flex;
	justify-content: space-between;
	gap: 20px;
	margin: 10px auto;
	padding: 0 20px;

	@media screen and (max-width: 640px) {
		display: grid;
		grid-template-columns: 1fr 1fr;
		padding: 0;
		margin: 6px auto 0;
		grid-row-gap: 10px;
		grid-column-gap: 12px;

		& > *:nth-of-type(3) {
			max-width: none;
			grid-column: span 2;
		}
	}
`;

export const ModalSharePicks: React.FC = observer(() => {
	const {
		i18n,
		isOpen,
		close,
		shareImagePath,
		isMobileShareVisible,
		shareViaMobile,
		shareViaFB,
		shareViaTW,
		onCopyImage,
		isCodeCopied,
		downloadImage,
	} = useViewController<IShareController>(Bindings.ShareController);
	// const isMobile = useMediaQuery(`(max-width: ${media.mobile})`);

	return (
		<Modal open={isOpen}>
			<ModalContent>
				<CloseBlock onClick={close}>
					<CloseIcon />
				</CloseBlock>
				<Typography variant={"h3"}>
					{i18n.t("modals.share_picks.title", "Path to the Playoffs Submitted")}
				</Typography>

				<Description>
					<b>{i18n.t("modals.share_picks.descr_part_1", "Congrats")}</b>
					{" - "}
					{i18n.t(
						"modals.share_picks.descr_part_2",
						"you have been entered into the draw to win Super Bowl tickets!"
					)}
				</Description>

				<ImgBox>
					{shareImagePath ? (
						<img
							id="share_image"
							src={`${SITE_URL}${shareImagePath}`}
							alt="share"
							width="100%"
						/>
					) : (
						<Preloader color={"#000"} />
					)}
				</ImgBox>

				<Exist when={isMobileShareVisible}>
					<ButtonRow>
						<PrimaryButton onClick={shareViaMobile}>
							<ShareIcon />
							<span>{i18n.t("modals.save_picks.button_share", "Share")}</span>
						</PrimaryButton>
						<PrimaryButtonInvert onClick={onCopyImage}>
							<CopyIcon />
							<span>
								<Exist when={!isCodeCopied}>
									{i18n.t("modals.save_picks.button_copy", "Copy image")}
								</Exist>
								<Exist when={isCodeCopied}>
									{i18n.t("modals.save_picks.button_copied", "Image Copied")}
								</Exist>
							</span>
						</PrimaryButtonInvert>
					</ButtonRow>
				</Exist>

				<Exist when={!isMobileShareVisible}>
					<ButtonRow>
						<ShareButton onClick={shareViaFB}>
							<FacebookIcon />
							<span>{i18n.t("modals.save_picks.button_share_fb", "Facebook")}</span>
						</ShareButton>
						<ShareButton onClick={shareViaTW}>
							<TwitterIcon />
							<span>{i18n.t("modals.save_picks.button_share_tw", "Twitter")}</span>
						</ShareButton>
						<PrimaryButtonInvert onClick={partial(downloadImage, shareImagePath)}>
							<CopyIcon />
							<span>
								<Exist when={!isCodeCopied}>
									{i18n.t("modals.save_picks.button_copy", "Copy image")}
								</Exist>
								<Exist when={isCodeCopied}>
									{i18n.t("modals.save_picks.button_copied", "Image Copied")}
								</Exist>
							</span>
						</PrimaryButtonInvert>
					</ButtonRow>
				</Exist>
			</ModalContent>
		</Modal>
	);
});
